import { Mail } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Alert, AlertColor, AlertTitle, Box, Stack } from "@mui/material";
import { UpgradeSubscriptionButton } from "components/action-buttons/action-buttons";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import {
  PermissionsWrapper,
  PermissionTooltipWrapper,
} from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import {
  getActiveOrganisation,
  Organisation,
  useIdentity,
} from "contexts/identity-context";
import * as QueryKeys from "data";
import { InvitationAlertState, Role, UserMembership } from "data/models";
import { submitOrganisationInvitations } from "data/mutations";
import { fetchOrganisationMemberships, fetchSubscription } from "data/queries";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { formatTimestamp } from "utils";
import { resolveIdentityId } from "utils/identity";
import { InvitationItem, MemberInvitationModal } from "./invite-members";
import { InvitationList } from "./organisation-invitation-list";

interface AlertState {
  message: string;
  severity: AlertColor;
}

export const OrganisationMembers = () => {
  const [identityState] = useIdentity();
  const currentOrganisation: Organisation | false =
    getActiveOrganisation(identityState);

  const queryClient = useQueryClient();
  const [invitationModalOpen, setInvitationModalOpen] = useState(false);
  const [alertStateList, setAlertStateList] = useState<AlertState[]>([]);
  const [showAlert, setShowAlert] = useState(false);
  const [showGiftedCollaborationAlert, setShowGiftedCollaborationAlert] =
    useState(true);

  useEffect(() => {
    if (showAlert) {
      const timeId = setTimeout(() => {
        setShowAlert(false);
      }, 5000);

      return () => {
        clearTimeout(timeId);
      };
    }
  }, [showAlert]);

  const currentOrg = currentOrganisation as Organisation;
  const membersQuery = useQuery(
    [QueryKeys.organisationInvitations, currentOrg?.organisationId],
    () => fetchOrganisationMemberships(currentOrg!.organisationId),
    {
      enabled: !!currentOrganisation,
    }
  );

  const invitationMutation = useMutation(
    (invitations: InvitationItem[]) =>
      submitOrganisationInvitations(invitations, currentOrg!.organisationId),
    {
      onSuccess: (alerts: InvitationAlertState[]) => {
        setAlertStateList(alerts);
        setShowAlert(true);
        setInvitationModalOpen(false);
        // Refetch the members list
        queryClient.invalidateQueries(QueryKeys.organisationInvitations);
      },
    }
  );

  const identityId = resolveIdentityId(identityState);

  const subscriptionQuery = useQuery([QueryKeys.subscription, identityId], () =>
    fetchSubscription()
  );

  const activeSub = subscriptionQuery.data?.activeSubscription;
  const hasScheduledUpgradeOrDowngrade = activeSub?.nextSub != null;
  const hasScheduledCancellation = activeSub?.cancelAtPeriodEnd === true;

  const futurePlanPermissionKey = subscriptionQuery.data?.activeSubscription
    ?.nextSub?.plan.product_key
    ? [subscriptionQuery.data.activeSubscription?.nextSub.plan.product_key]
    : [];

  const {
    isPermitted: futurePlanIsPermitted,
    isPermissionsSuccess: isFuturePlanPermissionsSuccess,
  } = usePermissions({
    permissionSets: futurePlanPermissionKey,
  });

  const { getAdminOverride } = usePermissions();

  const hasGiftedCollaboration =
    getAdminOverride(PermissionsKeys.membersCreate) === "allow";

  const onAdminInitiatedTrial =
    subscriptionQuery.data?.activeSubscription?.trialType == "admin";

  const isInvitationsListLoaded = membersQuery.isSuccess;

  const isSubscriptionReady = subscriptionQuery.isSuccess;

  const isFuturePlanReady = isFuturePlanPermissionsSuccess;

  const hasBlockingCollaboration =
    hasScheduledCancellation ||
    (hasScheduledUpgradeOrDowngrade &&
      !futurePlanIsPermitted([PermissionsKeys.membersCreate]));

  const hasOwner = getOwner(membersQuery.data ?? []);

  const showCollaborationEndingAlert =
    isInvitationsListLoaded &&
    isSubscriptionReady &&
    !onAdminInitiatedTrial &&
    isFuturePlanReady &&
    hasBlockingCollaboration &&
    hasOwner &&
    !hasGiftedCollaboration;

  return (
    <Box>
      <MemberInvitationModal
        isOpen={invitationModalOpen}
        isLoading={invitationMutation.isLoading}
        isError={invitationMutation.isError}
        handleClose={() => setInvitationModalOpen(false)}
        handleSubmit={(itemList: InvitationItem[]) =>
          invitationMutation.mutate(itemList)
        }
        orgName={(currentOrganisation as Organisation).name}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          position: "relative",
          top: "-50px",
        }}
      >
        <PermissionTooltipWrapper
          permissionKeys={[PermissionsKeys.membersCreate]}
        >
          <LoadingButton
            loading={membersQuery.isLoading}
            onClick={() => setInvitationModalOpen(true)}
            disabled={!currentOrganisation || invitationMutation.isLoading}
            startIcon={<Mail />}
            variant="contained"
          >
            Invite Members
          </LoadingButton>
        </PermissionTooltipWrapper>
      </Box>

      {hasGiftedCollaboration && showGiftedCollaborationAlert && (
        <Alert
          variant="outlined"
          severity="success"
          sx={{ marginBottom: 2 }}
          onClose={() => setShowGiftedCollaborationAlert(false)}
        >
          This organisation has been gifted Collaboration by Geoscape Support.
          You can now invite other members to join your organisation.
        </Alert>
      )}

      {!hasGiftedCollaboration && (
        <PermissionsWrapper
          keys={[PermissionsKeys.membersCreate]}
          messages={{
            [PermissionsReasons.ROLE_DENY]: <></>,
            [PermissionsReasons.UPGRADE_REQUIRED]: (
              <Alert
                variant="outlined"
                severity="info"
                action={
                  <UpgradeSubscriptionButton
                    variant="text"
                    color="inherit"
                    style={{
                      justifyContent: "flex-end",
                      textAlign: "right",
                      width: 140,
                    }}
                    text="Review Plans"
                  />
                }
              >
                <AlertTitle>Ready to Grow Your Team?</AlertTitle>
                Unlock the ability to invite members to your organisation by
                upgrading to our Team or Pro plan.
              </Alert>
            ),
          }}
        >
          <PermissionsWrapper
            keys={[PermissionsKeys.membersCreate]}
            messages={{
              [PermissionsReasons.ROLE_DENY]: <></>,
              [PermissionsReasons.UPGRADE_REQUIRED]: (
                <Alert
                  variant="outlined"
                  severity="info"
                  action={
                    <UpgradeSubscriptionButton
                      variant="text"
                      color="inherit"
                      style={{
                        justifyContent: "flex-end",
                        textAlign: "right",
                        width: 140,
                      }}
                      text="Review Plans"
                    />
                  }
                >
                  <AlertTitle>Ready to Grow Your Team?</AlertTitle>
                  Unlock the ability to invite members to your organisation by
                  upgrading to our Team or Pro plan.
                </Alert>
              ),
            }}
          ></PermissionsWrapper>
          <PermissionsWrapper
            keys={[PermissionsKeys.rolesUpdate]}
            messages={{
              [PermissionsReasons.ROLE_DENY]: <></>,
              [PermissionsReasons.UPGRADE_REQUIRED]: (
                <Alert
                  variant="outlined"
                  severity="info"
                  action={
                    <UpgradeSubscriptionButton
                      variant="text"
                      color="inherit"
                      style={{
                        justifyContent: "flex-end",
                        textAlign: "right",
                        width: 140,
                      }}
                      text="Review Plans"
                    />
                  }
                >
                  <AlertTitle>Unlock Roles & Permissions with Pro</AlertTitle>
                  Roles and Permissions allow you to define specific access
                  levels for your team members. This feature is included in our
                  Pro plan.
                </Alert>
              ),
            }}
          ></PermissionsWrapper>
        </PermissionsWrapper>
      )}
      {showCollaborationEndingAlert && (
        <>
          <Alert
            variant="outlined"
            severity="warning"
            sx={{ marginTop: "16px" }}
            action={
              <UpgradeSubscriptionButton
                variant="text"
                color="inherit"
                style={{
                  justifyContent: "flex-end",
                  textAlign: "left",
                  width: 140,
                }}
                text="Review Plans"
              />
            }
          >
            <AlertTitle>
              Upcoming Change: Collaboration Ending{" "}
              {formatTimestamp(
                (subscriptionQuery.data.activeSubscription?.renewalDate ?? 0) *
                  1000
              )}
            </AlertTitle>
            Please note: Your plan is scheduled to downgrade on{" "}
            {formatTimestamp(
              (subscriptionQuery.data.activeSubscription?.renewalDate ?? 0) *
                1000
            )}
            . This will result in the loss of your organisation's collaboration
            features, and only the owner will be able to use Geoscape Hub.
          </Alert>
        </>
      )}

      {showAlert && renderAlerts(alertStateList)}
      <PermissionsWrapper
        keys={[PermissionsKeys.membersRead]}
        messages={{
          [PermissionsReasons.UPGRADE_REQUIRED]: <></>,
        }}
      >
        <InvitationList
          memberships={membersQuery.data ?? []}
          isLoading={membersQuery.isLoading}
        />
      </PermissionsWrapper>
    </Box>
  );
};

const getOwner = (invitationList: UserMembership[]): string | null => {
  const findByRole = (role: Role): string | undefined =>
    invitationList.find((invitation) => invitation.role == role)?.userEmail;

  return findByRole(Role.OWNER) || null;
};

const renderAlerts = (alertStateList: AlertState[]) => {
  return (
    <Stack sx={{ width: "100%" }} spacing={2}>
      {alertStateList
        .filter((m) => m.message)
        .map((alert: AlertState, index) => (
          <Alert key={index} variant="filled" severity={alert.severity}>
            {alert.message}
          </Alert>
        ))}
    </Stack>
  );
};
